/**
 * The gallery entries that live in dashboard-render (Fase D, 2026-10-24).
 *
 * Moved out of `ShowcaseGallery.tsx` so the builder's gallery page can
 * merge them with the kit's and bi-components' entries without pulling
 * the old gallery component along. `DASHBOARD_RENDER_ENTRIES` keeps its
 * name and its `ShowcaseEntry` shape.
 */
import { Bookmark, Mail, Sparkles } from "lucide-react";
import { useState } from "react";

import type { ShowcaseEntry } from "@pervasivemind/bi-components/showcase-manifest";

import { CollapsibleFilterHeader } from "./CollapsibleFilterHeader";
import { ControlPanel } from "./ControlPanel";
import { DashboardTabs } from "./DashboardTabs";
import { DateRangeSlider } from "./DateRangeSlider";
import { useLang } from "./i18n-binding";
import { resolveLocalized } from "./i18n-resolve";
import { triggerClass } from "./GenericFilterBar";
import { NumberRangeSlider } from "./NumberRangeSlider";
import type { RangeHistogram } from "./number-range";
import { REPORT_ACTION_CLASS, REPORT_ACTION_PRIMARY_CLASS, ReportHeader } from "./ReportHeader";
import { ResponsiveCardGrid, MIN_CARD_PX } from "./ResponsiveCardGrid";
import { ScopeStrip } from "./ScopeStrip";
import type { DashboardFilter, DashboardTab } from "./types";
import {
  DemoAntiJump,
  DemoChartDataChange,
  DemoDrillAccordion,
  DemoDrillInPlace,
  DemoFadeSwap,
  DemoViewSlide,
  DemoHeightTransition,
  DemoNumberTween,
  DemoRefreshOverlay,
  DemoScorecardMeters,
  DemoSkeletonToContent,
  DemoSwrCycle,
} from "./motion/gallery-demos";

// ── dashboard-render–local showcase entries ─────────────────
// The manifest at ``@pervasivemind/bi-components/showcase-manifest``
// only catalogs bi-components exports — its parity check governs THAT
// package's barrel. Widgets that live in THIS package (dashboard-
// render) can't be listed there without a dependency cycle
// (bi-components must not import dashboard-render), so they are
// surfaced here and merged into the same category grid below. Typed as
// ``ShowcaseEntry`` so they flow through the identical card / section
// render path. The parity script never sees them (it regex-scans the
// manifest .jsx file, not this component), so nothing here can trip
// ``check-manifest-parity.mjs``.

// Real gating-data domain (2021-01-04 → 2026-06-08), weekly snap —
// mirrors the bounds ``fetch_filter_options`` serves for the gating
// reports so the demo matches what ships.
const DATE_SLIDER_DEMO_BOUNDS = ["2021-01-04", "2026-06-08"] as const;

/** Stateful wrapper so the fully-controlled ``DateRangeSlider`` drags
 *  in the gallery (mirrors the ``showcase-demos`` pattern the
 *  bi-components filter widgets use — a no-op ``onChange`` renders them
 *  inert). Value is the same ``{"start","end"}`` ISO JSON the filter
 *  bar stores. */
function DateRangeSliderDemo(): JSX.Element {
  const [value, setValue] = useState<string>(() =>
    JSON.stringify({ start: "2023-01-02", end: "2025-12-29" }),
  );
  return (
    <div className="pt-1">
      <DateRangeSlider
        value={value}
        onChange={setValue}
        bounds={DATE_SLIDER_DEMO_BOUNDS}
        step="week"
      />
    </div>
  );
}

/** Stateful wrapper for the numeric ``range`` filter widget. Bounds and
 *  value are the shapes the engine serves / stores: a 0..1 fraction
 *  column shown as percent, opened at 30 % (the Dashboard Explorer
 *  3P-share default). */
function NumberRangeSliderDemo(): JSX.Element {
  const [value, setValue] = useState<string>('{"min":0.3}');
  // A fixed, illustrative distribution (not data): the real widget asks
  // the engine for the rows of the filter's ``histogram_table``.
  const fetchHistogram = (edges: number[]): Promise<RangeHistogram> => {
    const bins = Array.from({ length: edges.length + 1 }, (_, i) =>
      Math.round(20 + 30 * Math.sin(i / 3) ** 2 + (i > edges.length - 3 ? 120 : 0)),
    );
    const total = bins.reduce((a, b) => a + b, 0);
    return Promise.resolve({ bins, nulls: 0, total, in_range: Math.round(total * 0.74) });
  };
  return (
    <div className="max-w-[12rem] space-y-2 pt-1">
      <NumberRangeSlider
        value={value}
        onChange={setValue}
        bounds={["-0.0242", "1"]}
        format="percent"
        label="3P share"
        triggerClassName={triggerClass(false, false)}
        fetchHistogram={fetchHistogram}
      />
      <code className="block text-2xs text-neutral-500">value = {value || '""'}</code>
    </div>
  );
}


/** ResponsiveCardGrid at three widths, side by side — the width matrix
 *  that did not exist when a board tile squeezed four country cards to
 *  139px each. The point of the demo is that you can SEE the column
 *  count drop instead of having to deploy and drag a tile. */
function DemoResponsiveCardGrid(): JSX.Element {
  const CARDS = ["US · 90.8%", "CANADA · 94.0%", "MEXICO · 32.0%", "BRAZIL · 32.5%"];
  const widths = [308, 632, 1080];
  return (
    <div className="flex flex-col gap-4">
      <p className="text-xs text-neutral-500">
        Authored <code>card_columns = 2</code> is a CEILING. The grid drops a
        column rather than letting a card fall under {MIN_CARD_PX}px, where its
        label stops fitting.
      </p>
      {widths.map((w) => (
        <div key={w}>
          <p className="mb-1 text-xs font-semibold text-neutral-600">
            {w}px
          </p>
          <div style={{ width: w, maxWidth: "100%" }} className="rounded-lg border border-neutral-200 p-2">
            <ResponsiveCardGrid maxColumns={2} gapPx={8}>
              {CARDS.map((c) => (
                <div
                  key={c}
                  className="truncate rounded-md border border-neutral-200 bg-white px-3 py-2 text-xs font-semibold"
                >
                  {c}
                </div>
              ))}
            </ResponsiveCardGrid>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Report head and controls (look v3, Fase C7) ──────────────
// The chrome every report page shares, with neutral sample data (D13).
// The actions are the product's (D29): date, send, saved filters and the
// AI analysis as the primary one.
function DemoReportHeader() {
  return (
    <ReportHeader
      breadcrumb={[{ label: "Reportes", href: "#" }, { label: "Marca A", href: "#" }, { label: "Disponibilidad" }]}
      title="Disponibilidad y cobertura"
      description="Qué está disponible, qué se cayó y dónde falta surtido · Marca A"
      actions={
        <>
          <span className="text-2xs text-neutral-500">Fecha 20/10/2026</span>
          <button type="button" className={REPORT_ACTION_CLASS}>
            <Mail size={13} aria-hidden="true" />
            Enviar
          </button>
          <button type="button" className={REPORT_ACTION_CLASS}>
            <Bookmark size={13} aria-hidden="true" />
            Filtros guardados
          </button>
          <button type="button" className={REPORT_ACTION_PRIMARY_CLASS}>
            <Sparkles size={13} aria-hidden="true" />
            Análisis IA
          </button>
        </>
      }
    />
  );
}

const DEMO_SCOPE_FILTERS: DashboardFilter[] = [
  { key: "view", column: "view", type: "tabs", label: { es: "Vista", en: "View", pt: "Visão" }, option_labels: { sku: { es: "Por SKU", en: "By SKU", pt: "Por SKU" } } },
  { key: "window", column: "window", type: "tabs", label: { es: "Período", en: "Period", pt: "Período" } },
  { key: "retailer", column: "retailer", type: "multi_select", label: { es: "Retailers", en: "Retailers", pt: "Varejistas" } },
  { key: "brand", column: "brand", type: "select", label: { es: "Marcas", en: "Brands", pt: "Marcas" } },
] as DashboardFilter[];

const DEMO_TABS: DashboardTab[] = [
  { id: "summary", label: { es: "Resumen", en: "Summary", pt: "Resumo" }, section_ids: [] },
  { id: "retailers", label: { es: "Retailers", en: "Retailers", pt: "Varejistas" }, section_ids: [] },
  { id: "products", label: { es: "Productos", en: "Products", pt: "Produtos" }, section_ids: [] },
];

function DemoControlPanel() {
  const [tab, setTab] = useState("summary");
  return (
    <ControlPanel>
      <ScopeStrip
        filters={DEMO_SCOPE_FILTERS}
        activeFilters={{ view: "sku", window: "30d", retailer: "", brand: "Marca A" }}
      />
      <div className="mt-3">
        <DashboardTabs tabs={DEMO_TABS} activeId={tab} onChange={setTab} />
      </div>
    </ControlPanel>
  );
}

// A report group in the cajón: the «Productos» tab does not use the two
// pinned filters. They stay in their place, disabled, dashed and struck
// through, and the line under them names them on every tab, so moving
// between tabs moves nothing (2026-10-25).
const DEMO_PINNED_FILTERS: DashboardFilter[] = [
  { ...DEMO_SCOPE_FILTERS[0]!, pinned: true, options: ["sku", "pub"], option_labels: { sku: { es: "Por SKU", en: "By SKU", pt: "Por SKU" }, pub: { es: "Por publicación", en: "By listing", pt: "Por anúncio" } } },
  { ...DEMO_SCOPE_FILTERS[1]!, pinned: true, options: ["7d", "30d", "90d"] },
  DEMO_SCOPE_FILTERS[3]!,
] as DashboardFilter[];

function DemoGroupFilterDrawer() {
  const lang = useLang();
  const [tab, setTab] = useState("summary");
  const [active, setActive] = useState<Record<string, string>>({ view: "sku", window: "30d" });
  const current = DEMO_TABS.find((t) => t.id === tab);
  return (
    <ControlPanel>
      <CollapsibleFilterHeader
        filters={DEMO_PINNED_FILTERS}
        filterOptions={{ brand: ["Marca A", "Marca B"] }}
        activeFilters={active}
        onFilterChange={(key, value) => setActive((prev) => ({ ...prev, [key]: value }))}
        onClear={() => setActive({ view: "sku", window: "30d" })}
        onResetFilter={(f) => setActive((prev) => ({ ...prev, [f.key]: "" }))}
        today={new Date("2026-10-25T00:00:00Z")}
        notApplicable={{
          keys: new Set(tab === "products" ? ["view", "window"] : []),
          reportLabel: resolveLocalized(current?.label ?? "", lang) || tab,
        }}
        reserveNotApplicableLine
        surface="light"
      />
      <div className="mt-3">
        <DashboardTabs tabs={DEMO_TABS} activeId={tab} onChange={setTab} />
      </div>
    </ControlPanel>
  );
}

export const DASHBOARD_RENDER_ENTRIES: ShowcaseEntry[] = [
  {
    id: "report-header",
    componentName: "ReportHeader",
    category: "report",
    title: "Report head",
    subtitle:
      "Breadcrumb, title in the display face, description and the product actions (date, send, saved filters, AI analysis)",
    customLayout: true,
    render: () => <DemoReportHeader />,
  },
  {
    id: "control-panel",
    componentName: "ControlPanel",
    category: "report",
    title: "Control panel with scope and tabs",
    subtitle:
      "The white panel that holds the report's controls; the scope strip reads the active filters and the tabs open the group's reports",
    customLayout: true,
    render: () => <DemoControlPanel />,
  },
  {
    id: "group-filter-drawer",
    componentName: "CollapsibleFilterHeader",
    category: "report",
    title: "Filter drawer in a report group",
    subtitle:
      "A pinned filter the open tab does not use stays in its place, disabled, dashed and struck through; the line below names it on every tab, so switching tabs moves nothing (open «Productos»)",
    customLayout: true,
    render: () => <DemoGroupFilterDrawer />,
  },
  {
    id: "responsive-card-grid",
    componentName: "ResponsiveCardGrid",
    category: "analytics",
    title: "ResponsiveCardGrid — columns from the space available",
    subtitle:
      "Authored card_columns is a ceiling; the grid drops a column instead of squeezing a card below its readable width",
    customLayout: true,
    render: () => <DemoResponsiveCardGrid />,
  },
  {
    id: "date-range-slider",
    componentName: "DateRangeSlider",
    category: "dates",
    title: "Date Range Slider",
    subtitle:
      "Dual-handle date-window filter (filter_type date_slider) — drag either handle over the column's date domain",
    customLayout: true,
    render: () => <DateRangeSliderDemo />,
  },
  {
    id: "number-range-slider",
    componentName: "NumberRangeSlider",
    category: "filters",
    title: "Number Range Slider",
    subtitle:
      "Min-max metric threshold (filter_type range): a select-height trigger; the floating panel holds the histogram, the slider and exact inputs",
    customLayout: true,
    render: () => <NumberRangeSliderDemo />,
  },

  // ── Transitions (dashboard-render side, motion-polish PR-A) ──
  // The motion PRIMITIVES + composed patterns. The bi-components half
  // (tokens, spinners, progress hairline, shimmer) comes from the
  // manifest above; these are appended into the same "transitions"
  // category. Each demo carries its own TransitionDemo frame (replay +
  // reduced-motion toggle + Used-in / How / Export).
  {
    id: "motion-scorecard-meters",
    componentName: "MeterBar",
    category: "transitions",
    title: "Scorecard Meters — Value Change",
    subtitle:
      "MeterBar eases to its new value with transform: scaleX (compositor-only, no layout)",
    customLayout: true,
    render: () => <DemoScorecardMeters />,
  },
  {
    id: "motion-fade-swap",
    componentName: "FadeSwap",
    category: "transitions",
    title: "FadeSwap — Data Crossfade",
    subtitle: "Old content fades out in place while the new fades in — no unmount, no layout shift",
    customLayout: true,
    render: () => <DemoFadeSwap />,
  },
  {
    id: "motion-view-slide",
    componentName: "ViewSlide",
    category: "transitions",
    title: "ViewSlide — Cambio de vista",
    subtitle:
      "La tabla sale por un lado y el detalle entra por el otro; volver corre el mismo gesto invertido",
    customLayout: true,
    render: () => <DemoViewSlide />,
  },
  {
    id: "motion-number-tween",
    componentName: "NumberTween",
    category: "transitions",
    title: "NumberTween — KPI Count-up",
    subtitle: "KPI values roll to their new value with stable digit widths",
    customLayout: true,
    render: () => <DemoNumberTween />,
  },
  {
    id: "motion-refresh-overlay",
    componentName: "RefreshOverlay",
    category: "transitions",
    title: "RefreshOverlay — Card Refreshing",
    subtitle: "Dim + shimmer over mounted content, with a 150ms appear delay so cache hits never flash",
    customLayout: true,
    render: () => <DemoRefreshOverlay />,
  },
  {
    id: "motion-height-transition",
    componentName: "HeightTransition",
    category: "transitions",
    title: "HeightTransition — Animated Height",
    subtitle: "Measured height changes animate instead of snapping (the one legit layout move)",
    customLayout: true,
    render: () => <DemoHeightTransition />,
  },
  {
    id: "motion-skeleton-to-content",
    componentName: "DashboardSkeleton",
    category: "transitions",
    title: "Skeleton → Content (first load)",
    subtitle: "Placeholder only when there is NOTHING to show yet; content fades in when data lands",
    customLayout: true,
    label: "DashboardSkeleton",
    render: () => <DemoSkeletonToContent />,
  },
  {
    id: "motion-swr-cycle",
    componentName: "SwrCycle",
    category: "transitions",
    title: "Stale-While-Revalidate — Full Cycle",
    subtitle: "Mini-dashboard: change a filter and watch the whole choreography (synthetic demo — PR-B wires it for real)",
    customLayout: true,
    label: "SWR pattern",
    render: () => <DemoSwrCycle />,
  },
  // The two row-drill modes, chosen per report (`row_drill_mode`).
  // Swap is the DEFAULT (what a report that omits the field does);
  // the accordion is the explicit opt-in. Both cards say so.
  {
    id: "motion-drill-in-place",
    componentName: "RowDrillPanel",
    category: "transitions",
    title: "Drill In-Place (row_drill_mode: swap — default)",
    subtitle:
      "Row click swaps the cell's content for the drill panel — 150ms rise-in, cell size constant. The default when the table omits row_drill_mode",
    customLayout: true,
    render: () => <DemoDrillInPlace />,
  },
  {
    id: "motion-drill-accordion",
    componentName: "RowDrillPanel",
    category: "transitions",
    title: "Drill Accordion (row_drill_mode: accordion — opt-in)",
    subtitle:
      "Row click expands the detail UNDER the row, inside the table's own scroll — the card never grows, the row stays visible. Opt-in per report",
    customLayout: true,
    label: "DataTable + RowDrillPanel",
    render: () => <DemoDrillAccordion />,
  },
  {
    id: "motion-chart-data-change",
    componentName: "ChartDataChange",
    category: "transitions",
    title: "Chart Data Change (tier 1)",
    subtitle: "Whole-plot crossfade keyed by data hash — the universal default for every chart type",
    customLayout: true,
    label: "FadeSwap + chart",
    render: () => <DemoChartDataChange />,
  },
  {
    id: "motion-anti-jump",
    componentName: "AntiJumpSelection",
    category: "transitions",
    title: "Anti-Jump: Ring vs Border",
    subtitle: "Selection must never resize the box — inset ring, not an added border",
    customLayout: true,
    label: "selection rule",
    render: () => <DemoAntiJump />,
  },
];

